import { useEffect, useRef, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ProjectsSortBy } from "@/lib/get-projects";
import type { GetProjectsResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { getProjects } from "@/lib/get-projects";
import { scrollToBottom } from "@/lib/utils/scroll-to-bottom";
import { getConsoleAuthToken } from "./use-console-auth-token";

export const useProjectsList = (
  type: "my-projects" | "featured-projects",
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
) => {
  const [projects, setProjects] = useState<
    Array<GetProjectsResponseBody["projects"][number]>
  >([]);
  const pageRef = useRef(0);
  const [totalProjects, setTotalProjects] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [sortBy, setSortBy] = useState<ProjectsSortBy>("updatedAt");

  const {
    isLoading: isInitialLoading,
    refetch: refetchInitialProjects,
    isSuccess: isInitialSuccess,
    error: initialError,
  } = useQuery({
    enabled: true,
    queryKey: [type, debouncedSearch, sortBy],
    queryFn: async () => {
      try {
        const fetchedProjects = await getProjects({
          type,
          page: 0,
          search: debouncedSearch,
          sortBy,
          token: await getConsoleAuthToken(),
        });

        setProjects(fetchedProjects.projects);
        setTotalProjects(fetchedProjects.total);
        return fetchedProjects;
      } catch (error) {
        console.error("Error fetching projects", error);
        return {
          projects: [],
          total: 0,
        };
      }
    },
  });

  const {
    mutate: loadMoreProjects,
    isPending: isPageLoading,
    isSuccess: isPageSuccess,
    error: pageError,
  } = useMutation({
    mutationFn: async () => {
      pageRef.current += 1;

      const fetchedProjects = await getProjects({
        type,
        page: pageRef.current,
        sortBy,
        search: debouncedSearch,
        token: await getConsoleAuthToken(),
      });

      return fetchedProjects;
    },
    onSuccess: (data) => {
      setProjects([...projects, ...data.projects]);
      setTotalProjects(data.total);
      if (scrollContainerRef.current) {
        scrollToBottom(
          scrollContainerRef as React.RefObject<HTMLDivElement>,
          100,
        );
      }
    },
    onError: (error) => {
      console.error("Error fetching projects", error);
    },
  });

  useEffect(() => {
    setProjects([]);
    setTotalProjects(0);
    pageRef.current = 0;

    refetchInitialProjects();
  }, [debouncedSearch, refetchInitialProjects, sortBy]);

  return {
    projects,
    totalProjects,
    loadMoreProjects,
    isInitialLoading,
    isPageLoading,
    isInitialSuccess,
    isPageSuccess,
    setSearch,
    search,
    setSortBy,
    sortBy,
    initialError,
    pageError,
  };
};
